import type {
  AnswerStepResponse,
  BattleDto,
  BattleSummaryDto,
  CheckStepResponse,
  CompleteStepResponse,
  ContractDto,
  CourseDto,
  CreateModuleRequest,
  CreateModuleResponse,
  FrameCatalogEntryDto,
  FrameDto,
  LessonDto,
  LoginRequest,
  MeResponse,
  ModuleDetailDto,
  ModuleDto,
  OwnedPartDto,
  PipelineReportDto,
  PlayerProfileDto,
  RegisterRequest,
  ScenarioDto,
  UpdateSettingsRequest,
  ValidateRequest,
} from '@codeframe/api-contract';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from './client.js';

export const keys = {
  me: ['me'] as const,
  courses: ['courses'] as const,
  lesson: (id: string) => ['lesson', id] as const,
  frames: ['frames'] as const,
  catalog: ['frames', 'catalog'] as const,
  parts: ['parts'] as const,
  modules: ['modules'] as const,
  module: (id: string) => ['modules', id] as const,
  contracts: ['contracts'] as const,
  scenarios: ['scenarios'] as const,
  battles: ['battles'] as const,
  battle: (id: string) => ['battles', id] as const,
};

export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: async () => {
      try {
        return await api<MeResponse>('GET', '/auth/me');
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 30_000,
  });
}

/** The signed-in player's profile (the shell guarantees a session exists). */
export function usePlayer(): PlayerProfileDto | undefined {
  return useMe().data?.player;
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LoginRequest) => api<MeResponse>('POST', '/auth/login', body),
    onSuccess: (me) => qc.setQueryData(keys.me, me),
  });
}

export function useRegister() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RegisterRequest) => api<MeResponse>('POST', '/auth/register', body),
    onSuccess: (me) => qc.setQueryData(keys.me, me),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>('POST', '/auth/logout', {}),
    onSuccess: () => {
      qc.clear();
      qc.setQueryData(keys.me, null);
    },
  });
}

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateSettingsRequest) => api<PlayerProfileDto>('PATCH', '/player/settings', body),
    onSuccess: (player) => qc.setQueryData<MeResponse | null>(keys.me, (me) => (me ? { ...me, player } : me)),
  });
}

/** Rewards can level the player up and unlock features: refresh everything that depends on it. */
function useProgressInvalidation() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: keys.me });
    void qc.invalidateQueries({ queryKey: keys.courses });
    void qc.invalidateQueries({ queryKey: keys.contracts });
    void qc.invalidateQueries({ queryKey: keys.scenarios });
  };
}

// Academy -------------------------------------------------------------------------------

export function useCourses() {
  return useQuery({ queryKey: keys.courses, queryFn: () => api<CourseDto[]>('GET', '/academy/courses') });
}

export function useLesson(id: string) {
  return useQuery({ queryKey: keys.lesson(id), queryFn: () => api<LessonDto>('GET', `/academy/lessons/${id}`) });
}

function useStepMutation<TBody, TResult extends { stepCompleted: boolean; lessonCompleted: boolean }>(
  lessonId: string,
  action: 'check' | 'answer' | 'complete',
) {
  const qc = useQueryClient();
  const progress = useProgressInvalidation();
  return useMutation({
    mutationFn: ({ stepId, body }: { stepId: string; body: TBody }) =>
      api<TResult>('POST', `/academy/lessons/${lessonId}/steps/${stepId}/${action}`, body),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: keys.lesson(lessonId) });
      if (result.stepCompleted) void qc.invalidateQueries({ queryKey: keys.courses });
      if (result.lessonCompleted) progress();
    },
  });
}

export const useCheckStep = (lessonId: string) => useStepMutation<{ code: string }, CheckStepResponse>(lessonId, 'check');
export const useAnswerStep = (lessonId: string) => useStepMutation<{ choice: number }, AnswerStepResponse>(lessonId, 'answer');
export const useCompleteStep = (lessonId: string) => useStepMutation<Record<string, never>, CompleteStepResponse>(lessonId, 'complete');

// Workshop & Library ----------------------------------------------------------------------

export function useContracts(enabled = true) {
  return useQuery({ queryKey: keys.contracts, queryFn: () => api<ContractDto[]>('GET', '/workshop/contracts'), enabled });
}

export function useValidate() {
  return useMutation({ mutationFn: (body: ValidateRequest) => api<PipelineReportDto>('POST', '/workshop/validate', body) });
}

export function useModules() {
  return useQuery({ queryKey: keys.modules, queryFn: () => api<ModuleDto[]>('GET', '/library/modules') });
}

export function useModule(id: string | undefined) {
  return useQuery({
    queryKey: keys.module(id ?? ''),
    queryFn: () => api<ModuleDetailDto>('GET', `/library/modules/${id}`),
    enabled: !!id,
  });
}

export function useCreateModule() {
  const qc = useQueryClient();
  const progress = useProgressInvalidation();
  return useMutation({
    mutationFn: (body: CreateModuleRequest) => api<CreateModuleResponse>('POST', '/library/modules', body),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: keys.modules });
      if (r.rewards) progress();
    },
  });
}

export function useNewVersion(moduleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { source: string; contractId?: string }) => api<CreateModuleResponse>('POST', `/library/modules/${moduleId}/versions`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.modules });
      void qc.invalidateQueries({ queryKey: keys.frames });
    },
  });
}

export function useArchiveModule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>('DELETE', `/library/modules/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.modules }),
  });
}

// Frames -----------------------------------------------------------------------------------

export function useFrames() {
  return useQuery({ queryKey: keys.frames, queryFn: () => api<FrameDto[]>('GET', '/frames') });
}

export function useActiveFrame(): FrameDto | undefined {
  return useFrames().data?.find((f) => f.isActive);
}

export function useFrameCatalog() {
  return useQuery({ queryKey: keys.catalog, queryFn: () => api<FrameCatalogEntryDto[]>('GET', '/frames/catalog') });
}

export function useInstall() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ frameId, moduleId, slotIndex }: { frameId: string; moduleId: string; slotIndex: number }) =>
      api<FrameDto>('PUT', `/frames/${frameId}/software`, { moduleId, slotIndex }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.frames });
      void qc.invalidateQueries({ queryKey: keys.modules });
    },
  });
}

export function useUninstall() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ frameId, slotIndex }: { frameId: string; slotIndex: number }) => api<FrameDto>('DELETE', `/frames/${frameId}/software/${slotIndex}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.frames });
      void qc.invalidateQueries({ queryKey: keys.modules });
    },
  });
}

export function useParts() {
  return useQuery({ queryKey: keys.parts, queryFn: () => api<OwnedPartDto[]>('GET', '/parts') });
}

export function useInstallHardware() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ frameId, slot, partInstanceId }: { frameId: string; slot: string; partInstanceId: string | null }) =>
      api<FrameDto>('PUT', `/frames/${frameId}/hardware`, { slot, partInstanceId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.frames });
      void qc.invalidateQueries({ queryKey: keys.parts });
    },
  });
}

// Arena ------------------------------------------------------------------------------------

export function useScenarios() {
  return useQuery({ queryKey: keys.scenarios, queryFn: () => api<ScenarioDto[]>('GET', '/arena/scenarios') });
}

export function useBattles() {
  return useQuery({ queryKey: keys.battles, queryFn: () => api<BattleSummaryDto[]>('GET', '/arena/battles') });
}

export function useBattle(id: string) {
  return useQuery({ queryKey: keys.battle(id), queryFn: () => api<BattleDto>('GET', `/arena/battles/${id}`), staleTime: Infinity });
}

export function useStartBattle() {
  const qc = useQueryClient();
  const progress = useProgressInvalidation();
  return useMutation({
    mutationFn: (scenarioId: string) => api<BattleDto>('POST', '/arena/battles', { scenarioId }),
    onSuccess: (battle) => {
      qc.setQueryData(keys.battle(battle.id), battle);
      void qc.invalidateQueries({ queryKey: keys.battles });
      if (battle.rewards) progress();
    },
  });
}
